import React from "react";
import type { Components } from "react-markdown";

export function slugify(children: React.ReactNode): string {
  if (!children) return "";
  let text = "";
  React.Children.forEach(children, (child) => {
    if (typeof child === "string") {
      text += child;
    } else if (React.isValidElement(child) && (child as React.ReactElement<any>).props.children) {
      text += slugify((child as React.ReactElement<any>).props.children);
    }
  });
  return text
    .toLowerCase()
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/[^\w-]+/g, "") // remove all non-word chars
    .replace(/--+/g, "-") // replace multiple hyphens
    .replace(/^-+/, "") // trim hyphens from start
    .replace(/-+$/, ""); // trim hyphens from end
}

export const sharedMarkdownComponents: Components = {
  h2: ({ children }) => (
    <h2
      id={slugify(children)}
      className="scroll-mt-24 mt-12 border-t border-[#e2d7c8] pt-8 text-[2rem] font-bold leading-tight text-[#111111] first:mt-0 first:border-t-0 first:pt-0"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      id={slugify(children)}
      className="scroll-mt-24 mt-12 border-t border-[#e2d7c8] pt-8 text-[2rem] font-bold leading-tight text-[#111111] first:mt-0 first:border-t-0 first:pt-0"
    >
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4
      id={slugify(children)}
      className="scroll-mt-24 mt-8 text-[1.22rem] font-semibold leading-7 text-[#171717]"
    >
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="my-5 text-[15px] leading-8 text-[#2f2a24] whitespace-pre-line">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-5 list-disc space-y-2 pl-6 text-[15px] leading-8 text-[#2f2a24] marker:text-[#ac274f]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-5 list-decimal space-y-2 pl-6 text-[15px] leading-8 text-[#2f2a24] marker:font-semibold marker:text-[#ac274f]">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  hr: () => <hr className="my-10 border-[#ddd2c1]" />,
  strong: ({ children }) => (
    <strong className="font-semibold text-[#111111]">{children}</strong>
  ),
  a: ({ children, href }) => {
    const isInternal = href?.startsWith("#");
    return (
      <a
        href={href}
        target={isInternal ? undefined : "_blank"}
        rel={isInternal ? undefined : "noopener noreferrer"}
        className="font-medium text-[#ac274f] underline decoration-[#d894a7] underline-offset-4 transition-colors hover:text-[#8d183d]"
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="my-6 border-l-4 border-[#c95d7d] bg-[#f3e7db] px-5 py-3 text-[15px] leading-7 text-[#3b3129]">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="rounded bg-[#efe4d7] px-1.5 py-0.5 text-[0.92em] text-[#7a1e3d]">
      {children}
    </code>
  ),
};

export const darkMarkdownComponents: Components = {
  h2: ({ children }) => (
    <h2
      id={slugify(children)}
      className="scroll-mt-24 mt-12 border-t border-zinc-700 pt-8 text-[2rem] font-bold leading-tight text-white first:mt-0 first:border-t-0 first:pt-0"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      id={slugify(children)}
      className="scroll-mt-24 mt-12 border-t border-zinc-700 pt-8 text-[2rem] font-bold leading-tight text-white first:mt-0 first:border-t-0 first:pt-0"
    >
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4
      id={slugify(children)}
      className="scroll-mt-24 mt-8 text-[1.22rem] font-semibold leading-7 text-gray-200"
    >
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="my-5 text-[15px] leading-8 text-gray-300 whitespace-pre-line">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-5 list-disc space-y-2 pl-6 text-[15px] leading-8 text-gray-300 marker:text-blue-400">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-5 list-decimal space-y-2 pl-6 text-[15px] leading-8 text-gray-300 marker:font-semibold marker:text-blue-400">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  hr: () => <hr className="my-10 border-zinc-700" />,
  strong: ({ children }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  a: ({ children, href }) => {
    const isInternal = href?.startsWith("#");
    return (
      <a
        href={href}
        target={isInternal ? undefined : "_blank"}
        rel={isInternal ? undefined : "noopener noreferrer"}
        className="font-medium text-blue-400 underline decoration-blue-400/50 underline-offset-4 transition-colors hover:text-blue-300"
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="my-6 border-l-4 border-blue-500 bg-zinc-800/50 px-5 py-3 text-[15px] leading-7 text-gray-300">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-[0.92em] text-pink-400">
      {children}
    </code>
  ),
};
